import { useState, useEffect, useRef } from "react";
import {
  Sparkles,
  Flame,
  Target,
  HandHeart,
  Leaf,
  Check,
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
  ChevronDown,
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Community from "../components/Community";
import BookingDialog from "../components/BookingDialog";
import { Toaster } from "../components/ui/sonner";
import { useScrollReveal } from "../lib/useScrollReveal";
import { BRAND } from "../lib/brand";

const WA_LINK = "https://wa.me/919867413477";

const NITU_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_luxury-yoga-studio/artifacts/drkvfknk_IMG_20260502_200949297~2.jpg";

/* ===================== 1. HERO ===================== */
const PageHero = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="about-hero"
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden text-[#F9F6F0]"
      style={{
        background:
          "linear-gradient(135deg, #1e3a26 0%, #2a4a30 60%, #1F4A38 100%)",
      }}
    >
      <div className="grain absolute inset-0" />
      <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center py-32">
        <span className="reveal inline-block mb-6 px-4 py-1.5 rounded-full bg-[#D4A24C]/15 border border-[#D4A24C]/40 text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-[#EAC98A] font-semibold">
          Founded with heart · Built with purpose
        </span>
        <h1 className="reveal font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] font-light">
          We Don't Just Teach Yoga.
          <br />
          We Transform{" "}
          <em className="italic font-normal text-[#EAC98A]">Lives.</em>
        </h1>
        <p className="reveal mt-8 max-w-2xl mx-auto text-[#F9F6F0]/85 leading-relaxed text-base md:text-lg font-light">
          Expert-led yoga coaching for real transformation — body, mind and soul.
          Every session, every student, 100%.
        </p>
      </div>

      <a
        href="#story"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#EAC98A]/85 flex flex-col items-center gap-2 animate-bounce"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">
          Discover our story
        </span>
        <ChevronDown size={18} />
      </a>
    </section>
  );
};

/* ===================== 2. OUR STORY ===================== */
const OurStory = () => {
  const ref = useScrollReveal();
  return (
    <section
      id="story"
      ref={ref}
      data-testid="our-story"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
          Our Story
        </p>
        <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light leading-[1.05]">
          Why <em className="italic text-[#D4A24C]">Yogiinit</em> Was Born
        </h2>

        <blockquote className="reveal mt-10 border-l-4 border-[#6E8B6E] pl-6 md:pl-8 font-serif-display italic text-xl md:text-2xl text-[#2C5F4A] leading-relaxed">
          "I come from an accounting background — numbers were my world. But
          somewhere inside me, yoga was calling. Building Yogiinit wasn't easy.
          There were real ups and downs. But I knew this journey was worth every
          bit of it."
        </blockquote>

        <div className="mt-10 space-y-5 text-[#2C5F4A] text-[15.5px] leading-relaxed">
          <p className="reveal">
            Yogiinit did not come from a polished background in wellness or a
            ready-made roadmap. <strong className="text-[#153326]">Nitu Yadav</strong>{" "}
            built it from scratch — collecting knowledge, learning every domain,
            and showing up even when the path felt unclear.
          </p>
          <p className="reveal">
            There were days filled with doubt. Moments when the weight of building
            something new felt heavy. But she never walked it alone. Her friends,
            her family — and especially her mother — stood beside her through every
            challenge, every low moment, every comeback. That love is woven into
            everything Yogiinit stands for today.
          </p>
          <p className="reveal">
            Yogiinit is a growing yoga institute — and Nitu's biggest dream is for
            you to be part of this journey. Every student who walks in becomes
            family. Every handshake is a promise of real, honest, heart-led
            transformation.
          </p>
        </div>

        <div className="reveal mt-10 flex flex-wrap gap-2.5">
          {[
            "Supported by Family",
            "Built from the ground up",
            "Growing every day",
          ].map((t) => (
            <span
              key={t}
              className="text-[12px] tracking-wide px-4 py-2 rounded-full bg-[#1F4A38]/8 text-[#1F4A38] border border-[#1F4A38]/15 font-medium"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===================== 3. MEET THE FOUNDER ===================== */
const CERTS = [
  "200 Hr YTTC",
  "Hatha Yoga",
  "Power Yoga",
  "Props Yoga",
  "Pranayama",
  "Meditation",
  "Yogic Concepts",
  "Anatomy",
  "Yogic Diet",
  "Basic & Advanced Asanas",
  "Chakra Healing",
];

const MeetFounder = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="meet-founder"
      className="py-24 md:py-32 bg-[#F0E9DB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <article className="reveal grid grid-cols-1 lg:grid-cols-[380px_1fr] rounded-[32px] overflow-hidden bg-white shadow-[0_24px_70px_rgba(31,74,56,0.15)]">
          {/* Left panel */}
          <div
            className="relative grain p-10 flex flex-col justify-end min-h-[480px] lg:min-h-[640px]"
            style={{
              background:
                "linear-gradient(160deg, #1e3a26 0%, #2a4a30 70%, #1F4A38 100%)",
            }}
          >
            <span
              aria-hidden
              className="absolute inset-0 flex items-center justify-center font-serif-display font-bold pointer-events-none select-none"
              style={{
                fontSize: "14rem",
                color: "rgba(255,255,255,0.04)",
                lineHeight: 1,
              }}
            >
              NY
            </span>
            <img
              src={NITU_PORTRAIT}
              alt="Nitu Yadav — Founder of Yogiinit"
              loading="lazy"
              className="absolute inset-6 rounded-[24px] object-cover w-[calc(100%-3rem)] h-[calc(100%-9rem)]"
              style={{ borderTopRightRadius: "60px" }}
            />
            <div className="relative z-10 pt-6">
              <p className="font-serif-display text-3xl text-[#F9F6F0]">
                Nitu Yadav
              </p>
              <p className="mt-1 text-[10.5px] tracking-[0.32em] uppercase text-[#EAC98A] font-semibold">
                Founder · Certified Yoga Instructor
              </p>
            </div>
          </div>

          {/* Right panel */}
          <div className="p-9 md:p-14 bg-white">
            <p className="text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-4 font-medium">
              Meet the Founder
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[#153326] font-light leading-[1.08]">
              The Heart Behind <em className="italic">Yogiinit</em>
            </h2>

            <div className="mt-7 space-y-4 text-[#2C5F4A] text-[15.5px] leading-relaxed">
              <p>
                Nitu Yadav is not just a yoga instructor — she is a transformation
                guide. What makes her truly different is not just her certifications
                or training, but what she brings to every session: her whole heart.
              </p>
              <p>
                When Nitu teaches, she is fully present — only for you. There is no
                distraction, no going through the motions. She believes deeply in an
                honest relationship between teacher and student — no shortcuts, no
                pretence, just genuine care and real effort. What you see is what
                you get, always.
              </p>
              <p>
                Nitu learns every single day. She studies how to guide better, coach
                deeper and serve her students more fully. Her growth never stops —
                because she believes a teacher who stops learning stops giving.
              </p>
            </div>

            <blockquote className="mt-8 border-l-4 border-[#D4A24C] rounded-r-2xl bg-[#F9F6F0] p-6 font-serif-display italic text-lg md:text-xl text-[#153326] leading-relaxed">
              "Yoga is not about touching your toes. It is about what you learn on
              the way down — patience, persistence and the power of showing up for
              yourself."
              <footer className="mt-3 not-italic font-sans-body text-sm text-[#2C5F4A]/80">
                — Nitu Yadav, Founder, Yogiinit
              </footer>
            </blockquote>

            <div className="mt-8 rounded-2xl bg-[#E8EFE3] border border-[#1F4A38]/10 p-6">
              <p className="text-[11px] tracking-[0.3em] uppercase text-[#1F4A38] font-semibold mb-3">
                Yog Power International · Certified Areas
              </p>
              <div className="flex flex-wrap gap-2">
                {CERTS.map((c) => (
                  <span
                    key={c}
                    className="text-[12px] px-3 py-1.5 rounded-full bg-white text-[#1F4A38] border border-[#1F4A38]/15"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

/* ===================== 4. WHAT WE BELIEVE IN ===================== */
const BELIEFS = [
  {
    Icon: Leaf,
    title: "Holistic Wellness",
    desc:
      "True wellness is not about weight alone. At Yogiinit, we nurture your body, calm your mind and strengthen your spirit — all at once, together.",
  },
  {
    Icon: Flame,
    title: "Real Transformation",
    desc:
      "No shortcuts. No temporary fixes. We are committed to lasting change through structured, consistent and personalised practice that delivers results you feel.",
  },
  {
    Icon: Target,
    title: "Personalised Approach",
    desc:
      "Every student receives a program designed around their unique body, goals and life. A practice built for you always works better than one built for everyone.",
  },
  {
    Icon: HandHeart,
    title: "Pure Intention",
    desc:
      "Nitu's mission is to give her absolute best — without looking for the fruit. Her purpose is service, not just success. That purity is felt in every single class.",
  },
];

const Beliefs = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="beliefs"
      className="relative overflow-hidden text-[#F9F6F0]"
      style={{
        background:
          "linear-gradient(160deg, #1e3a26 0%, #1F4A38 60%, #153326 100%)",
      }}
    >
      <div className="grain absolute inset-0" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-2xl mb-14">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            Our Philosophy
          </p>
          <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05]">
            What We <em className="italic text-[#EAC98A]">Believe</em> In
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {BELIEFS.map(({ Icon, title, desc }) => (
            <div
              key={title}
              className="reveal rounded-[24px] bg-white/6 backdrop-blur-md border border-white/10 p-7 hover:bg-white/12 hover:-translate-y-1 hover:border-[#D4A24C]/30 transition-all duration-500"
            >
              <div className="h-12 w-12 rounded-2xl bg-[#D4A24C]/18 text-[#D4A24C] flex items-center justify-center mb-5">
                <Icon size={22} strokeWidth={1.6} />
              </div>
              <h3 className="font-serif-display text-2xl">{title}</h3>
              <p className="mt-3 text-[14px] text-[#F9F6F0]/80 leading-relaxed">
                {desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===================== 5. PROGRAMS ===================== */
const PROGRAMS = [
  {
    num: "01",
    title: "Private Yoga Sessions",
    desc:
      "One-on-one coaching with Nitu — fully personalised, flexible scheduling and undivided attention to your progress from the very first day.",
  },
  {
    num: "02",
    title: "Online Yoga Coaching",
    desc:
      "Transform from anywhere in the world. Yogiinit's expert coaching comes directly to your home, on your schedule, with zero compromise on quality.",
  },
  {
    num: "03",
    title: "Group Classes",
    desc:
      "Experience the energy of a community. Join our group sessions in Mumbai for an inspiring, supportive space that keeps you motivated and consistent.",
  },
];

const Programs = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="programs"
      className="py-24 md:py-32 bg-[#F0E9DB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            Programs
          </p>
          <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light">
            How We <em className="italic">Work</em> With You
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
          {PROGRAMS.map((p) => (
            <article
              key={p.num}
              className="reveal group relative rounded-[20px] bg-white p-8 md:p-9 shadow-[0_12px_36px_rgba(31,74,56,0.08)] hover:shadow-[0_22px_50px_rgba(31,74,56,0.14)] hover:-translate-y-1.5 transition-all duration-500 overflow-hidden"
            >
              <span
                aria-hidden
                className="font-serif-display text-[3.5rem] leading-none text-[#D4A24C]/35 font-medium block"
              >
                {p.num}
              </span>
              <h3 className="mt-3 font-serif-display text-2xl md:text-3xl text-[#153326] font-medium">
                {p.title}
              </h3>
              <p className="mt-4 text-[#2C5F4A] leading-relaxed text-[15px]">
                {p.desc}
              </p>
              <span className="absolute left-0 bottom-0 h-[3px] w-0 bg-[#D4A24C] group-hover:w-full transition-all duration-500" />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===================== 6. WHAT MAKES YOGIINIT DIFFERENT ===================== */
const DIFFERENTIATORS = [
  "Certified instructor who gives 100% in every session, for every student",
  "Complete honesty — a clean, transparent bond between you and Nitu",
  "Structured, goal-oriented programs built around your needs",
  "Traditional yoga wisdom blended with modern wellness principles",
  "Personalised coaching — Nitu learns your body, your pace, your goals",
  "Available Online and Offline — Mumbai and beyond",
  "A teacher who never stops learning — so you never stop growing",
  "Open-hearted approach — Nitu always gives from the heart, genuinely",
];

const Different = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="different"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            Why Choose Us
          </p>
          <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light">
            What Makes <em className="italic">Yogiinit</em> Different
          </h2>
          <p className="reveal mt-5 text-[#2C5F4A]/85 font-light">
            Eight gentle promises we keep with every student, every session.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {DIFFERENTIATORS.map((d) => (
            <div
              key={d}
              className="reveal flex items-start gap-4 rounded-[20px] bg-[#F9F6F0] border border-[#1F4A38]/10 p-5 md:p-6 hover:bg-white hover:border-[#D4A24C]/40 hover:shadow-[0_10px_30px_rgba(31,74,56,0.08)] transition-all duration-400"
            >
              <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#6E8B6E]/20 text-[#1F4A38] shrink-0">
                <Check size={16} strokeWidth={2.5} />
              </span>
              <p className="text-[#153326] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===================== 7. STATS BAR ===================== */
const STATS = [
  { kind: "text", value: "Still", suffix: "Growing", label: "& Always Will Be" },
  { kind: "number", target: 561, suffix: "", label: "YouTube Family" },
  { kind: "number", target: 421, suffix: "", label: "Instagram Community" },
  { kind: "number", target: 4, suffix: "+", label: "Structured Programs" },
  { kind: "number", target: 100, suffix: "%", label: "Heart in Every Class" },
];

// Animated count-up hook — eases from 0 to `target` when ref enters viewport.
const useCountUp = (target, duration = 2200) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const startTime = performance.now();
            const tick = (now) => {
              const elapsed = now - startTime;
              const t = Math.min(elapsed / duration, 1);
              // easeOutCubic
              const eased = 1 - Math.pow(1 - t, 3);
              setValue(Math.round(target * eased));
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            io.unobserve(node);
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [target, duration]);

  return [ref, value];
};

const CountStat = ({ target, suffix, label }) => {
  const [ref, value] = useCountUp(target);
  return (
    <div ref={ref} className="px-4 md:px-6 text-center first:border-l-0 first:pl-0">
      <p className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-[#FFF8E7] font-medium leading-tight tabular-nums">
        {value}
        {suffix}
      </p>
      <p className="mt-2 text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[#FFF8E7]/85 font-medium">
        {label}
      </p>
    </div>
  );
};

const StatsBar = () => {
  return (
    <section
      data-testid="stats-bar"
      className="relative overflow-hidden text-[#F9F6F0]"
      style={{
        background: "linear-gradient(90deg, #C8A855 0%, #D4A24C 50%, #C4714A 100%)",
      }}
    >
      <div className="grain absolute inset-0 opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-16">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-8 divide-x divide-white/20">
          {STATS.map((s, i) => {
            if (s.kind === "text") {
              return (
                <div key={i} className="px-4 md:px-6 text-center first:border-l-0 first:pl-0">
                  <p className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-[#FFF8E7] font-medium leading-tight">
                    {s.value}
                    <br />
                    <span className="italic font-light">{s.suffix}</span>
                  </p>
                  <p className="mt-2 text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[#FFF8E7]/85 font-medium">
                    {s.label}
                  </p>
                </div>
              );
            }
            return (
              <CountStat
                key={i}
                target={s.target}
                suffix={s.suffix}
                label={s.label}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ===================== 8. VISION & MISSION ===================== */
const VisionMission = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="vision-mission"
      className="py-24 md:py-32 bg-[#F0E9DB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
        {/* Vision */}
        <article
          className="reveal relative overflow-hidden rounded-[28px] p-9 md:p-12 text-[#F9F6F0] shadow-[0_18px_50px_rgba(31,74,56,0.25)] hover:-translate-y-1.5 transition-all duration-500"
          style={{
            background:
              "linear-gradient(160deg, #1F4A38 0%, #1e3a26 70%, #153326 100%)",
          }}
        >
          <span
            aria-hidden
            className="absolute -right-6 -bottom-10 font-serif-display font-bold pointer-events-none select-none"
            style={{
              fontSize: "18rem",
              color: "rgba(255,255,255,0.04)",
              lineHeight: 0.9,
            }}
          >
            V
          </span>
          <p className="relative text-[11px] tracking-[0.35em] uppercase text-[#EAC98A] mb-5 font-semibold">
            Our Vision
          </p>
          <h3 className="relative font-serif-display text-3xl md:text-4xl font-light leading-[1.1]">
            A Global <em className="italic text-[#EAC98A]">Yogiinit</em>
          </h3>
          <p className="relative mt-6 text-[#F9F6F0]/90 leading-relaxed text-[15.5px]">
            Nitu's dream is to take Yogiinit far beyond Mumbai — with branches
            across India and eventually around the world, so that real, honest,
            heart-led yoga coaching reaches every person who needs it. Alongside
            this, <strong className="text-[#EAC98A]">Yogiinit.store</strong> is a
            growing vision she is working to bring to life as soon as possible —
            a space where wellness extends beyond the mat.
          </p>
        </article>

        {/* Mission */}
        <article
          className="reveal relative overflow-hidden rounded-[28px] p-9 md:p-12 text-[#F9F6F0] shadow-[0_18px_50px_rgba(58,42,30,0.25)] hover:-translate-y-1.5 transition-all duration-500"
          style={{
            background:
              "linear-gradient(160deg, #4a3526 0%, #3a2a1e 70%, #2c2017 100%)",
          }}
        >
          <span
            aria-hidden
            className="absolute -right-6 -bottom-10 font-serif-display font-bold pointer-events-none select-none"
            style={{
              fontSize: "18rem",
              color: "rgba(255,255,255,0.04)",
              lineHeight: 0.9,
            }}
          >
            M
          </span>
          <p className="relative text-[11px] tracking-[0.35em] uppercase text-[#E8C39A] mb-5 font-semibold">
            Our Mission
          </p>
          <h3 className="relative font-serif-display text-3xl md:text-4xl font-light leading-[1.1]">
            Service Before <em className="italic text-[#E8C39A]">Self</em>
          </h3>
          <p className="relative mt-6 text-[#F9F6F0]/90 leading-relaxed text-[15.5px]">
            Nitu's mission is beautifully simple — to give her absolute best to
            every student, every session, without looking for the fruit. Not for
            money, not for recognition. Just to truly serve. She learns every day,
            she grows every day, and she brings all of that learning directly back
            to her students — because that is what a real teacher does.
          </p>
        </article>
      </div>
    </section>
  );
};

/* ===================== 9. CONTACT DETAILS ===================== */
const CONTACT = [
  {
    Icon: Phone,
    label: "Call / WhatsApp",
    value: "+91 98674 13477",
    sub: "Mon – Sun",
    href: WA_LINK,
  },
  {
    Icon: Mail,
    label: "Email",
    value: "hello@yogiinit.com",
    sub: "We reply within hours",
    href: "mailto:hello@yogiinit.com",
  },
  {
    Icon: MapPin,
    label: "Location",
    value: "Toutlespace Malad, Mumbai 400064",
    sub: "Online worldwide",
    href: "#",
  },
  {
    Icon: Clock,
    label: "Timings",
    value: "Flexible Schedule",
    sub: "Morning · Evening · Weekend",
    href: "#",
  },
];

const ContactDetails = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="contact-details"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            Reach Us
          </p>
          <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light">
            Contact <em className="italic">Details</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {CONTACT.map(({ Icon, label, value, sub, href }) => (
            <a
              key={label}
              href={href}
              target={href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className="reveal block rounded-[24px] bg-[#F9F6F0] border border-[#1F4A38]/10 p-7 hover:bg-white hover:border-[#D4A24C]/40 hover:shadow-[0_14px_36px_rgba(31,74,56,0.1)] hover:-translate-y-1 transition-all duration-500"
            >
              <div className="h-12 w-12 rounded-2xl bg-[#1F4A38]/8 text-[#1F4A38] flex items-center justify-center mb-5">
                <Icon size={20} strokeWidth={1.6} />
              </div>
              <p className="text-[11px] tracking-[0.28em] uppercase text-[#D4A24C] font-semibold mb-2">
                {label}
              </p>
              <p className="font-serif-display text-xl text-[#153326] leading-snug">
                {value}
              </p>
              <p className="mt-1.5 text-xs text-[#2C5F4A]/70 tracking-wide">
                {sub}
              </p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===================== 10. CTA ===================== */
const AboutCTA = ({ onBook }) => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="about-cta"
      className="relative overflow-hidden text-[#F9F6F0]"
      style={{
        background:
          "linear-gradient(135deg, #1e3a26 0%, #2a4a30 60%, #1F4A38 100%)",
      }}
    >
      <div className="grain absolute inset-0" />
      <div className="relative max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
        <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] font-light">
          Ready to Begin Your{" "}
          <em className="italic text-[#EAC98A]">Transformation?</em>
        </h2>
        <p className="reveal mt-7 max-w-xl mx-auto text-[#F9F6F0]/85 font-light text-base md:text-lg">
          Take the first, small step. We'll walk the rest with you — patiently,
          honestly, fully present.
        </p>

        <div className="reveal mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            type="button"
            onClick={onBook}
            data-testid="about-cta-book"
            className="inline-flex items-center justify-center gap-2 bg-[#D4A24C] hover:bg-[#C29344] text-[#153326] rounded-full px-8 py-4 text-sm font-semibold tracking-wide shadow-[0_18px_50px_rgba(212,162,76,0.35)] hover:shadow-[0_24px_60px_rgba(212,162,76,0.55)] transition-all duration-400 hover:-translate-y-0.5"
          >
            <MessageCircle size={18} strokeWidth={1.8} />
            Book a Free Consultation
          </button>
          <Link
            to="/classes"
            data-testid="about-cta-explore"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white/95 hover:bg-white hover:text-[#1F4A38] px-8 py-4 text-sm font-medium tracking-wide transition-all duration-400 backdrop-blur-sm"
          >
            Explore Our Programs
          </Link>
        </div>

        <p className="reveal mt-7 text-sm text-[#F9F6F0]/70 tracking-wide">
          {BRAND.contact.phone} · Mon – Sun
        </p>
      </div>
    </section>
  );
};

/* ===================== PAGE COMPOSITION ===================== */
const AboutPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);

  return (
    <div className="App">
      <Header onBook={openBooking} />
      <main>
        <PageHero />
        <OurStory />
        <MeetFounder />
        <Beliefs />
        <Programs />
        <Different />
        <StatsBar />
        <VisionMission />
        <ContactDetails />
        <AboutCTA onBook={openBooking} />
        <Community />
      </main>
      <Footer />
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
      <Toaster position="top-center" richColors />
    </div>
  );
};

export default AboutPage;
